import React, { createContext, FC, useEffect } from 'react';

import { useQuery } from '@apollo/client';
import Box from '@material-ui/core/Box';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Helmet } from 'react-helmet';
import { Route, Switch, useLocation } from 'react-router-dom';

import { Loader } from '../../components/Loader';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { AUTH_APP_ID_KEY } from '../../const';
import { GET_TENANT_INFO } from '../../graphql/tenant';
import { GET_LEARNER_INFO } from '../../graphql/user';
import { AuthErrorPage } from '../../pages/AuthErrorPage';
import { InvitePage } from '../../pages/InvitePage';
import Maintenance from '../../pages/Maintenance/Maintenance';
import { SubscriptionPage } from '../../pages/SubscriptionPage';
import { JOIN_TEAM_ROUTE, MAINTENANCE } from '../../routes';
import { LearnerInfo, Query, Tenant } from '../../types';
import { useAuth } from '../../utils/hooks';
import useInitGtm from '../../utils/hooks/useInitGtm';
import useInitUserCom from '../../utils/hooks/useInitUserCom';
import useScrollToTopOnRouteChange from '../../utils/hooks/useScrollToTopOnRouteChange';
import useUpdateUserComSubInfo from '../../utils/hooks/useUpdateUserComSubInfo';
import useVerse from '../../utils/hooks/useVerse';
import { Layout } from '../Layout';
import { RXDLayout } from '../RXDLayout';
import useStyles from './styles';

interface CommonLayoutProps {
  setAuthAppId: React.Dispatch<React.SetStateAction<string>>;
  hasAuthenticatedCookie: boolean;
}

export const TenantCtx = createContext<Tenant | null>(null);
export const UserInfoCtx = createContext<LearnerInfo | null | undefined>(null);

const CommonLayout: FC<CommonLayoutProps> = ({ setAuthAppId, hasAuthenticatedCookie }) => {
  const classes = useStyles();
  const { isInitialized, isAuthenticated, isLoading } = useAuth();
  const { onHotspotClick } = useVerse();

  const location = useLocation();

  useScrollToTopOnRouteChange();

  useEffect(() => {
    onHotspotClick((payload) => window.open(window.location.origin + payload));
  }, [onHotspotClick]);

  const { data: tenantData, loading: loadingTenant } = useQuery(GET_TENANT_INFO);
  const tenant = tenantData?.getTenantInfo;
  const customCss = tenant?.custom_css;

  const { data: learnerData, loading: learnerLoading, refetch: refetchUserInfo } = useQuery<
    Pick<Query, 'getLearnerInfo'>
  >(GET_LEARNER_INFO, {
    errorPolicy: 'all',
    skip: !isInitialized || !isAuthenticated,
  });

  const userInfo = learnerData?.getLearnerInfo;

  const chargeBeeSite = userInfo?.sub_provided_by_chargebee_site || tenant?.chargebee_site;

  useInitGtm(location, tenantData?.getTenantInfo?.gtm_id, userInfo, learnerLoading);
  useInitUserCom(location, userInfo);
  useUpdateUserComSubInfo(userInfo);

  useEffect(() => {
    const idFromStorage = localStorage.getItem(AUTH_APP_ID_KEY);
    const idFromRequest = tenant?.auth0_app_id || '';
    if (idFromStorage !== idFromRequest && idFromRequest) {
      setAuthAppId(idFromRequest);
      localStorage.setItem(AUTH_APP_ID_KEY, idFromRequest);
    }
  }, [tenant, setAuthAppId]);

  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles?.parentNode?.removeChild(jssStyles);
    }
  }, []);

  const waitForUser = hasAuthenticatedCookie && (!isInitialized || isLoading || learnerLoading);

  return (
    <TenantCtx.Provider value={tenant}>
      <UserInfoCtx.Provider value={userInfo}>
        <Box className={classes.container}>
          <CssBaseline />
          <Helmet>
            <style>{customCss}</style>
          </Helmet>
          <Switch>
            <Route
              path={JOIN_TEAM_ROUTE}
              render={(props) => (
                <InvitePage
                  {...props}
                  tenant={tenant}
                  userInfo={userInfo}
                  userLoading={learnerLoading}
                  refetchUserInfo={refetchUserInfo}
                  fallback={<Loader />}
                />
              )}
            />
            <ProtectedRoute
              exact
              path="/pricing/:id"
              withSubscription={false}
              userInfo={userInfo}
              userLoading={learnerLoading}
              render={(props) => (
                <SubscriptionPage
                  {...props}
                  logoUrl={tenant?.logo_url}
                  chargeBeeSite={chargeBeeSite}
                  userInfo={userInfo}
                  fallback={<Loader />}
                />
              )}
            />
            <Route
              path="/rxd"
              render={(props) => (
                <RXDLayout
                  tenant={tenant}
                  userInfo={userInfo}
                  userLoading={learnerLoading}
                  {...props}
                />
              )}
            />
            <Route path="/auth-error" render={(props) => <AuthErrorPage {...props} />} />
            <Route path={MAINTENANCE} render={(props) => <Maintenance {...props} />} />
            <Route
              path="/"
              render={(props) => (
                <Layout
                  tenant={tenant}
                  loadingTenant={loadingTenant}
                  userInfo={userInfo}
                  userLoading={learnerLoading}
                  waitForUser={waitForUser}
                  {...props}
                />
              )}
            />
          </Switch>
        </Box>
      </UserInfoCtx.Provider>
    </TenantCtx.Provider>
  );
};

export default CommonLayout;
